import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import type { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';

import { MetricSkeleton } from '@/components/feedback/metric-skeleton';
import { BudgetVarianceHighlight, DeliveryVarianceHighlight } from './delivery-variance-highlight';
import type { TimeFrameMode } from './time-frame-utils';

type ExpiredDailyKpisProps = {
  dateRange: ISODateRange;
  timeFrameMode: TimeFrameMode;
};

const modeLabels: Record<TimeFrameMode, string> = {
  pacing_blocks: 'End of block',
  months: 'End of month',
  lifetime: 'End of lifetime',
  last_7_days: 'Last 7 days'
};

export function ExpiredDailyKpis({ dateRange, timeFrameMode }: ExpiredDailyKpisProps) {
  const { lineItem } = useLineItemContext();

  const result = useCubeQuery({
    measures: [
      'line_item_metrics_daily.actual_units',
      'line_item_metrics_daily.planned_units',
      'line_item_metrics_daily.actual_spend',
      'line_item_metrics_daily.planned_spend'
    ],
    dimensions: ['line_items.unit_price_type_name'],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;

  const row = result.rows[0];
  if (!row) return null;

  const actualUnits = Number(row['line_item_metrics_daily.actual_units']);
  const plannedUnits = Number(row['line_item_metrics_daily.planned_units']);
  const unitPriceTypeName = row['line_items.unit_price_type_name'] as UnitPriceType['name'];
  const unitName = getUnitName(unitPriceTypeName).full || 'units';
  const formattedUnitsDiff = Math.abs(actualUnits - plannedUnits).toLocaleString();

  const actualSpend = Number(row['line_item_metrics_daily.actual_spend']);
  const plannedSpend = Number(row['line_item_metrics_daily.planned_spend']);
  const formattedSpendDiff = formatMoney(Math.abs(actualSpend - plannedSpend));

  return (
    <div className="border-y border-[#3c4f6a] bg-gradient-to-r from-transparent via-[rgba(60,79,106,0.1)] to-transparent py-6">
      <div className="flex items-center">
        <div className="flex flex-1 flex-col items-center">
          <span className="text-xs font-medium text-gray-500">
            {modeLabels[timeFrameMode]} {unitName}
          </span>
          <div className="mt-1">
            <DeliveryVarianceHighlight
              actual={actualUnits}
              planned={plannedUnits}
              formattedDifference={formattedUnitsDiff}
            />
          </div>
        </div>
        <div className="flex flex-1 flex-col items-center">
          <span className="text-xs font-medium text-gray-500">
            {modeLabels[timeFrameMode]} media budget
          </span>
          <div className="mt-1">
            <BudgetVarianceHighlight
              actual={actualSpend}
              planned={plannedSpend}
              formattedDifference={formattedSpendDiff}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
